import styled, { css } from 'styled-components';

import { Cross } from '@solent-university/solent-icons';
import ArrowButton from 'Elements/Button/Arrow';
import scrollbarStyle from 'Common/styles/scrollbar';

export const Container = styled.div<{ $scrollHeight: number }>`
    ${({ theme, $scrollHeight }) => css`
        ${scrollbarStyle('secondary')}
        display: flex;
        position: relative;
        padding: 0 0 0 0.5rem;
        border-radius: 0.875rem 0 0 0;
        max-height: 100%;
        overflow: auto;
        align-items: flex-start;

        @media ${theme.breakpoints.medium.media} {
            padding: 0 0 0 1.5rem;
        }

        &:before {
            content: '';
            position: absolute;
            top: 0rem;
            width: 100%;
            min-height: 100%;
            top: 1rem;
            height: calc(${$scrollHeight}px - 1rem);
            bottom: auto;
            left: 0;
            background-color: #fbfbfb; /* add to theme */
            border-radius: 0.875rem 0 0 0;
            z-index: -1;

            @media ${theme.breakpoints.medium.media} {
                top: 13rem;
                height: calc(${$scrollHeight}px - 13rem);
            }
        }
    `}
`;

export const DataColumns = styled.div<{ $colCount: number }>`
    ${({ theme, $colCount }) => css`
        ${scrollbarStyle('secondary')}
        display: grid;
        grid-template-columns: repeat(${$colCount}, 1fr);
        column-gap: 0.5rem;
        overflow-x: auto;
        padding: 6rem 0.5rem 0 0.25rem;

        @media ${theme.breakpoints.medium.media} {
            column-gap: 2rem;
            padding: 16rem 2rem 0 1rem;
        }
    `}
`;

export const Column = styled.div`
    ${({ theme }) => css`
        width: 9rem;
        display: flex;
        flex-direction: column;
        background: white; /* Add to theme */
        position: relative;
        box-shadow: ${theme.commonStyles.boxShadow};
        border-radius: 0.5rem;
        height: auto;

        @media ${theme.breakpoints.medium.media} {
            width: 21.5rem;
        }
    `}
`;

export const Cell = styled.span`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: 4rem;
    text-align: center;
    position: relative;
    width: 100%;
    padding: 0 0.5rem;
    line-height: 1.2;

    &:before {
        content: '';
        position: absolute;
        bottom: -0.0625rem;
        left: 100%;
        height: 0.0625rem;
        border-bottom: 0.0625rem dashed rgba(0, 0, 0, 0.15);
        width: 2rem; /* table column gap */
    }

    &:not(:last-child) {
        border-bottom: 0.0625rem solid rgba(0, 0, 0, 0.15); /* Add to theme */
    }
`;

export const Heading = styled(Cell)<{ $hidden?: boolean }>`
    font-weight: 600;
    justify-content: flex-start;
    text-align: left;
    padding-right: 1rem;

    ${({ $hidden }) =>
        $hidden &&
        css`
            display: none;
        `}
`;

export const HeadingText = styled.span`
    font-size: 1rem;
    opacity: 1;
    line-height: 1.1;
    transition: opacity 25ms ease 200ms, width 200ms ease;
`;

export const HeadingsColumn = styled(Column)<{ $isScrolling: boolean }>`
    ${({ theme, $isScrolling }) => css`
        width: 11rem;
        margin-top: 6rem;
        margin-right: 0.25rem;
        flex: 0 0 auto;
        background: linear-gradient(
            120deg,
            white 0%,
            #f9fafa 35%,
            #eef2f2 65%,
            #e8eded 100%
        ); /* Add to theme */

        /* Hide heading text on mobile when scrolling */

        /* stylelint-disable-next-line */
        @media only screen and (max-width: ${theme.breakpoints.medium.value - 1}px) {
            transition: width 200ms ease;

            ${$isScrolling &&
            css`
                width: 4rem;

                & ${HeadingText} {
                    width: 0;
                    opacity: 0;
                    overflow: hidden;
                }
            `}
        }

        @media ${theme.breakpoints.medium.media} {
            width: 19.25rem;
            margin-top: 16rem;
            margin-right: 1rem;
        }
    `}
`;

export const IconWrapper = styled.span`
    display: inline-flex;
    justify-content: center;
    align-items: center;
`;

export const Icon = styled.svg`
    ${({ theme }) => css`
        color: ${theme.colors.primary.hex};
        width: 1.25rem;
        height: 1.25rem;
        margin: 0 1rem;

        @media ${theme.breakpoints.medium.media} {
            margin: 0 1.5rem;
            width: 1.5rem;
            height: 1.5rem;
        }
    `}
`;

export const CourseName = styled(Cell)`
    ${({ theme }) => css`
        position: absolute;
        bottom: 100%;
        height: 5rem;
        z-index: 1;
        border-radius: 0.5rem;
        flex-direction: column;
        justify-content: flex-end;
        align-items: flex-start;
        font-weight: 600;
        text-align: left;
        padding: 0.5rem 0;
        line-height: 1.2;
        font-size: 0.875rem;

        @media ${theme.breakpoints.medium.media} {
            height: 13.75rem;
            background-color: rgba(0, 0, 0, 0.7); /*  todo use theme */
            color: white; /* use theme */
            box-shadow: ${theme.commonStyles.boxShadow};
            font-size: 1.5rem;
            padding: 0.75rem;
            margin-bottom: 0.75rem;

            &:after {
                content: '';
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                position: absolute;
                z-index: -1;
                border-radius: inherit;
                background: linear-gradient(
                    to top,
                    rgba(0, 0, 0, 0.9),
                    rgba(0, 0, 0, 0)
                ); /* add to theme */
            }
        }

        &:not(:last-child) {
            border: none;
        }

        &:before {
            content: none;
        }
    `}
`;

export const CourseImageWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -2;
    border-radius: inherit;
    overflow: hidden;
    display: none;

    @media ${({ theme }) => theme.breakpoints.medium.media} {
        display: block;
    }
`;

export const RemoveButton = styled.button`
    box-shadow: ${({ theme }) => theme.commonStyles.boxShadow};
    background: white; /* todo add to theme */
    border-radius: 50%;
    width: 2rem;
    height: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: none;
    position: absolute;
    transform: translate(50%, -50%);
    right: 0;
    top: 0;
`;

export const RemoveIcon = styled(Cross)`
    width: 1rem;
    height: 1rem;
`;

export const CourseLevel = styled(Cell)`
    font-weight: 600;

    @media ${({ theme }) => theme.breakpoints.medium.media} {
        font-size: 1.5rem;
    }
`;

export const Button = styled(ArrowButton)`
    margin-top: 1rem;
    width: 100%;
    font-size: 0.875rem;
    padding: 0.75rem 0.5rem;

    @media ${({ theme }) => theme.breakpoints.medium.media} {
        font-size: 1rem;
        padding: 1rem 1.5rem;
    }

    @media ${({ theme }) => theme.breakpoints.large.media} {
        font-size: 1.875rem;
    }
`;

export const RemoveTextButton = styled.button`
    border: none;
    background: none;
    text-decoration: underline;
    padding: 0 0.5rem;
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 0.75rem 0;

    &:hover {
        text-decoration: none;
    }
`;
